import React, { useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { CaretDownIcon, CaretRightIcon, ClipboardTextIcon, LightbulbIcon, WarningIcon } from "@phosphor-icons/react";

import { ActivityAnalytics } from "@axio/graphql/generated/graphql";
import { formatPercentage, getSentimentColor } from "../../../../utils/analyticsUtils";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import ObjectivePanel from "./ObjectivePanel";
import ProgressBar from "./ProgressBar";
import StatBadge from "./StatBadge";

interface ActivityPanelProps {
  activity?: ActivityAnalytics;
  index?: number;
  inactive?: boolean;
  loading?: boolean;
}

const ActivityPanel: React.FC<ActivityPanelProps> = ({ activity, index, inactive = false, loading = false }) => {
  const [expanded, setExpanded] = useState(false);

  // Format completion percentage correctly
  const formatCompletionPercentage = (value: number | undefined): string => {
    if (value === undefined || value === null) return formatPercentage(0);
    // If value is already in percentage form (> 1), don't multiply by 100
    return value > 1 ? formatPercentage(value) : formatPercentage(value * 100);
  };

  // Calculate completion for progress bar
  // TODO: This data point is not available. We need to get it from the backend. Right now completion_percentage is only calculate for objectives. that needs to be consolidated upstream to activities and then to modules.
  // const completionPercentage = activity?.completion_percentage
  //   ? activity.completion_percentage > 1
  //     ? activity.completion_percentage
  //     : activity.completion_percentage * 100
  //   : 0;
  const completionPercentage = 0;

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <CustomText text="Loading activity..." />
      </View>
    );
  }

  return (
    <View style={[styles.container, { opacity: inactive ? 0.6 : 1 }]}>
      <TouchableOpacity style={styles.header} onPress={() => !inactive && setExpanded(!expanded)} disabled={inactive}>
        <View style={styles.headerLeft}>
          {expanded ? (
            <CaretDownIcon size={16} color="#6B7280" style={styles.expandIcon} />
          ) : (
            <CaretRightIcon size={16} color="#6B7280" style={styles.expandIcon} />
          )}
          <CustomText
            text={activity?.label || `Activity ${(index ?? 0) + 1}`}
            weight="bold"
            useTranslationText={false}
          />
          {activity?.sentiment && (
            <View
              style={[
                styles.sentimentBadge,
                {
                  backgroundColor: getSentimentColor(activity.sentiment) + "20",
                  borderColor: getSentimentColor(activity.sentiment),
                },
              ]}>
              <CustomText
                text={activity.sentiment}
                size="xs"
                style={{ color: getSentimentColor(activity.sentiment), textTransform: "capitalize" }}
                useTranslationText={false}
              />
            </View>
          )}
        </View>
      </TouchableOpacity>

      {/* Always show progress bar and key metrics */}
      {completionPercentage > 0 && (
        <View style={styles.progressSection}>
          <View style={styles.progressHeader}>
            <CustomText text="Completion" size="xs" />
            <CustomText text={formatCompletionPercentage(completionPercentage)} size="xs" useTranslationText={false} />
          </View>
          <ProgressBar percentage={completionPercentage} size="sm" />
        </View>
      )}

      <View style={styles.metricsRow}>
        <StatBadge label="Success" value={activity?.success_points || 0} color="#10B981" />
        <StatBadge label="Confusion" value={activity?.confusion_points || 0} color="#EF4444" />
        <StatBadge label="Questions" value={activity?.questions_asked || 0} color="#3B82F6" />
        {activity?.active_interaction_count && activity.active_interaction_count > 0 && (
          <StatBadge label="Interactions" value={activity.active_interaction_count} color="#8B5CF6" />
        )}
        {/* {activity?.total_time_taken && activity.total_time_taken > 0 && (
          <StatBadge label="Time" value={formatTime(activity.total_time_taken)} color="#6B7280" />
        )} */}
      </View>

      {expanded && (
        <View style={styles.expandedContent}>
          <CustomSpacing type="vertical" size="m" />

          {/* Summaries */}
          {activity?.summaries && activity.summaries.length > 0 && (
            <View style={styles.summariesSection}>
              <View style={styles.sectionHeader}>
                <ClipboardTextIcon size={20} color="#3B82F6" />
                <CustomText text="Summaries" weight="bold" style={[styles.sectionTitle, { color: "#3B82F6" }]} />
              </View>
              <CustomSpacing type="vertical" size="s" />
              {activity.summaries.map((summary, i) => (
                <View key={i} style={styles.summaryItem}>
                  <CustomText text={summary} size="s" useTranslationText={false} />
                </View>
              ))}
            </View>
          )}

          {/* Recommendations */}
          {activity?.further_recommendations && activity.further_recommendations.length > 0 && (
            <View style={styles.recommendationsSection}>
              <View style={styles.sectionHeader}>
                <LightbulbIcon size={20} color="#3B82F6" />
                <CustomText text="Recommendations" weight="bold" style={[styles.sectionTitle, { color: "#3B82F6" }]} />
              </View>
              <CustomSpacing type="vertical" size="s" />
              <View style={styles.recommendationsGrid}>
                {activity.further_recommendations.map((rec, i) => (
                  <View key={i} style={styles.recommendationChip}>
                    <CustomText text={rec} size="xs" style={styles.recommendationText} useTranslationText={false} />
                  </View>
                ))}
              </View>
            </View>
          )}

          <CustomSpacing type="vertical" size="s" />

          {/* Success/Confusion Details */}
          {activity?.success_points_details?.length || activity?.confusion_points_details?.length ? (
            <View style={styles.detailsSection}>
              {activity.success_points_details && activity.success_points_details.length > 0 && (
                <View>
                  <View style={styles.sectionHeader}>
                    <ClipboardTextIcon size={20} color="#059669" />
                    <CustomText
                      text="Success Details"
                      weight="bold"
                      style={[styles.sectionTitle, { color: "#059669" }]}
                    />
                  </View>
                  <CustomSpacing type="vertical" size="s" />
                  {activity.success_points_details.map((detail, i) => (
                    <View key={i} style={styles.successDetailItem}>
                      <CustomText text={detail} size="s" useTranslationText={false} />
                    </View>
                  ))}
                </View>
              )}

              {activity.confusion_points_details && activity.confusion_points_details.length > 0 && (
                <View style={styles.confusionDetailsSection}>
                  <View style={styles.sectionHeader}>
                    <WarningIcon size={20} color="#DC2626" />
                    <CustomText
                      text="Confusion Details"
                      weight="bold"
                      style={[styles.confusionTitle, { color: "#DC2626" }]}
                    />
                  </View>
                  <CustomSpacing type="vertical" size="s" />
                  {activity.confusion_points_details.map((detail, i) => (
                    <View key={i} style={styles.confusionDetailItem}>
                      <CustomText text={detail} size="s" useTranslationText={false} />
                    </View>
                  ))}
                </View>
              )}
            </View>
          ) : null}

          {/* Objectives */}
          <View style={styles.objectivesSection}>
            <CustomText text="Objectives" weight="bold" style={styles.objectivesTitle} />
            <CustomSpacing type="vertical" size="s" />

            {activity?.details?.objectives && activity.details.objectives.length > 0 ? (
              <View style={styles.objectivesList}>
                {activity.details.objectives.map((objective, idx) => (
                  <ObjectivePanel key={objective.id || idx} objective={objective} />
                ))}
              </View>
            ) : (
              <CustomText text="No objectives for this activity." size="s" style={styles.noDataText} />
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
    marginLeft: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    padding: 12,
  },
  loadingContainer: {
    padding: 16,
    alignItems: "center",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  expandIcon: {
    marginRight: 8,
  },
  sentimentBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 12,
    borderWidth: 1,
    marginLeft: 8,
  },
  progressSection: {
    marginTop: 8,
  },
  progressHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  metricsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
  },
  expandedContent: {
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 12,
    marginTop: 12,
  },
  objectivesSection: {
    marginBottom: 16,
  },
  objectivesTitle: {
    color: "#3B82F6",
  },
  objectivesList: {
    gap: 8,
  },
  noDataText: {
    color: "#6B7280",
    fontStyle: "italic",
    paddingVertical: 8,
  },
  summariesSection: {
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  sectionTitle: {
    marginLeft: 8,
  },
  summaryItem: {
    marginBottom: 4,
    padding: 8,
    backgroundColor: "#EBF8FF",
    borderLeftWidth: 4,
    borderLeftColor: "#3B82F6",
    borderRadius: 4,
  },
  recommendationsSection: {
    marginBottom: 16,
  },
  recommendationsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  recommendationChip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: "#F0FDF4",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#10B981",
  },
  recommendationText: {
    color: "#059669",
  },
  detailsSection: {
    marginBottom: 16,
  },
  successTitle: {
    marginLeft: 8,
    color: "#059669",
  },
  successDetailItem: {
    marginBottom: 4,
    padding: 8,
    backgroundColor: "#F0FDF4",
    borderRadius: 4,
    borderLeftWidth: 4,
    borderColor: "#10B981",
  },
  confusionDetailsSection: {
    marginBottom: 16,
  },
  confusionTitle: {
    marginLeft: 8,
    color: "#DC2626",
  },
  confusionDetailItem: {
    marginBottom: 4,
    padding: 8,
    backgroundColor: "#FEF2F2",
    borderRadius: 4,
    borderLeftWidth: 4,
    borderColor: "#EF4444",
  },
});

export default ActivityPanel;
